import Titulo from '../../components/tipografia/Titulo';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiMagnify, mdiRefresh } from '@mdi/js';
import { DragDropContext } from 'react-beautiful-dnd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import SalaEspera from './SalaEspera';
import { toast } from 'react-toastify';
import Grid, { GridItem } from '../../components/layout/Grid';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import { useDebounce } from '../chat/useDebounce';
import FiltroGrupoUsuario from '../../components/Filtros/FiltroGrupoUsuario';
import Botao from '../../components/Botao';
import { useAuth } from '../../lib/useAuth';
import { useLigacaoLiveKit } from '../../hooks/useLigacaoLiveKit';
import Ligacao from '../chat/Ligacao';
import { io } from 'socket.io-client';

type Query = {
    grupo_id?: [string, any];
    online?: [string, any];
};

const reorder = (list: any, startIndex: number, endIndex: number): any => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const move = (
    source: any,
    destination: any,
    droppableSource: any,
    droppableDestination: any,
): any => {
    const sourceClone = Array.isArray(source) ? Array.from(source) : [];
    const destClone = Array.isArray(destination) ? Array.from(destination) : [];
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    if (removed) destClone.splice(droppableDestination.index, 0, removed);

    const result: any = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
};

type DroppableTarget =
    | { type: 'waiting' }
    | { type: 'group'; id: number }
    | { type: 'user'; id: number }
    | { type: 'unknown' };

const parseDroppableId = (droppableId: string): DroppableTarget => {
    if (droppableId === 'aguardando') {
        return { type: 'waiting' };
    }

    if (droppableId.startsWith('grupo-')) {
        const [, rawId] = droppableId.split('-');
        const id = Number(rawId);
        return Number.isFinite(id) ? { type: 'group', id } : { type: 'unknown' };
    }

    if (droppableId.startsWith('atendente-')) {
        const [, rawId] = droppableId.split('-');
        const id = Number(rawId);
        return Number.isFinite(id) ? { type: 'user', id } : { type: 'unknown' };
    }

    return { type: 'unknown' };
};

type PageProps = {};

type FilterProps = {
    somente_online: boolean;
    grupo_id: number[];
};

const Page = ({ }: PageProps) => {
    const { user } = useAuth();

    // Socket para o hook de ligação (mantido durante o ciclo de vida da página)
    const wsInstance = useRef<any>(null);
    if (!wsInstance.current) {
        wsInstance.current = io('/wsinternal', {
            timeout: 5000,
            autoConnect: true,
            withCredentials: true,
            transports: ['websocket'],
        });
    }
    const socket = wsInstance.current;

    const {
        ligacao,
        ligacaoState,
        aceitarLigacao,
        encerrarLigacao,
        toggleMute,
        toggleSpeaker,
        toggleMinimized,
    } = useLigacaoLiveKit(socket);

    const [dndList, setDndList] = useState<any>({});
    const [dragItem, setDragItem] = useState(null);

    const [search, setSearch] = useState<string>('');
    const debouncedSearch = useDebounce(search, 500);

    const [filter, setFilter] = useState<FilterProps>({
        somente_online: true,
        grupo_id: [],
    });

    const queryUsers = useQuery(
        ['usuarios', filter, debouncedSearch],
        async () => {
            const fi: Query = {};
            const q: Record<string, string> = {};

            if (filter?.grupo_id) {
                fi.grupo_id = ['in', filter.grupo_id];
            }

            if (filter?.somente_online) {
                q.somente_online = 'true';
            }

            const res = await fetch(
                `/api/usuario?t=200&txt=${encodeURIComponent(debouncedSearch)}&fi=${encodeURIComponent(JSON.stringify(fi))}&q=${encodeURIComponent(JSON.stringify(q))}`,
            );
            return res.json();
        },
        {
            staleTime: 1000 * 60 * 15,
            enabled: !dragItem,
        },
    );

    const queryGroups = useQuery(
        ['grupos'],
        async () => {
            const res = await fetch(`/api/usuario_grupo/box`);
            return res.json();
        },
        {
            staleTime: 1000 * 60 * 15,
            enabled: !dragItem,
        },
    );

    const queryList = useQuery(
        ['chamada/ativas'],
        async () => {
            const res = await fetch(`/api/chamada/ativas`);
            return res.json();
        },
        {
            enabled: queryUsers.isSuccess && !dragItem,
            staleTime: 1000 * 5,
            // O quadro agora reage a `fila_board.changed` via socket; o polling
            // fica como rede de segurança caso o socket caia.
            refetchInterval: 60000,
            onSuccess(data) {
                buildDndList(data.result);
            },
            onSettled(data, error) {
                console.log('onSettled chamadas ativas', data, error);
            },
        },
    );

    // Mantém o quadro reativo a mudanças (nova chamada, aceite, transferência,
    // encerramento, atendente on/offline) em vez de depender só do polling acima.
    const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Durante o drag as queries ficam com `enabled: false` para não brigar com o
    // estado local otimista, mas `refetch()` do react-query ignora `enabled` —
    // por isso os escopos pendentes só são aplicados quando o drag termina.
    const dragItemRef = useRef(dragItem);
    useEffect(() => {
        dragItemRef.current = dragItem;
    }, [dragItem]);

    const pendingEscoposRef = useRef<Set<string>>(new Set());

    const aplicarEscopos = useCallback(
        (escopos: Set<string>) => {
            if (escopos.has('chamada')) {
                queryList.refetch();
            }
            if (escopos.has('presenca')) {
                queryUsers.refetch();
            }
        },
        [queryList, queryUsers],
    );

    useEffect(() => {
        if (!dragItem && pendingEscoposRef.current.size) {
            const escopos = pendingEscoposRef.current;
            pendingEscoposRef.current = new Set();
            aplicarEscopos(escopos);
        }
    }, [dragItem, aplicarEscopos]);

    useEffect(() => {
        const onFilaBoardChanged = (data: { escopo?: string }) => {
            const escopo = data?.escopo ?? 'chamada';
            if (escopo !== 'chamada' && escopo !== 'presenca') return;

            if (dragItemRef.current) {
                pendingEscoposRef.current.add(escopo);
                return;
            }

            if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
            refetchTimeoutRef.current = setTimeout(() => {
                aplicarEscopos(new Set([escopo]));
            }, 400);
        };

        socket.on('fila_board.changed', onFilaBoardChanged);

        return () => {
            if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
            socket.off('fila_board.changed', onFilaBoardChanged);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    const transferMutator = useMutation(
        async (data: { id: number; usuario_id?: number; grupo_id?: number }) => {
            const res = await fetch(`/api/chamada/${data.id}/transferir`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    usuario_id: data.usuario_id,
                    grupo_id: data.grupo_id,
                }),
            });

            if (!res.ok) throw new Error('Erro ao transferir chamada');

            return res.json();
        },
        {
            onSettled: async () => {
                setDragItem(null);
                queryList.refetch();
            },
            onSuccess: async () => {
                toast('Chamada transferida com sucesso', {
                    toastId: 'success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
            },
            onError: async () => {
                toast('Erro ao transferir chamada. Tente novamente mais tarde.', {
                    toastId: 'error',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            },
        },
    );

    const buildDndList = useCallback(
        (list: any) => {
            const itens = Array.isArray(list) ? list : [];

            const listas: Record<string, any[]> = {
                aguardando: itens.filter(
                    (it: any) =>
                        it.usuario_responsavel_id == null &&
                        it.grupo_responsavel_id == null &&
                        (it.estado === 'aguardando' || it.estado === 'conectada'),
                ),
            };

            if (queryGroups?.data?.result) {
                for (const grupo of queryGroups.data.result) {
                    listas[`grupo-${grupo.id}`] = itens.filter(
                        (it: any) =>
                            it.grupo_responsavel_id === grupo.id &&
                            it.usuario_responsavel_id == null,
                    );
                }
            }

            if (queryUsers?.data?.result) {
                for (const user of queryUsers.data.result) {
                    listas[`atendente-${user.id}`] = itens.filter(
                        (it: any) =>
                            it.usuario_responsavel_id === user.id,
                    );
                }
            }

            setDndList(listas);
        },
        [queryGroups?.data?.result, queryUsers.data?.result],
    );

    useEffect(() => {
        if (queryList.data?.result) {
            buildDndList(queryList.data.result);
        }
    }, [queryList.data?.result, buildDndList]);

    const gruposComRamaisOnline = useMemo(() => {
        const grupos = new Set<number>();
        if (queryUsers.data?.result) {
            queryUsers.data.result.forEach((user: any) => {
                if (user.online && user.grupos) {
                    user.grupos.forEach((grupo: any) => {
                        grupos.add(grupo.id);
                    });
                }
            });
        }
        return grupos;
    }, [queryUsers.data?.result]);

    const onDragStart = useCallback(
        (result: any) => {
            setDragItem(dndList?.[result.source.droppableId]?.[result.source.index]);
        },
        [dndList],
    );

    const onDragEnd = useCallback(
        (result: any) => {
            const { source, destination, draggableId } = result;

            if (!destination) {
                setDragItem(null);
                return;
            }

            const destino = parseDroppableId(destination.droppableId);
            const origem = parseDroppableId(source.droppableId);

            if (destino.type === 'unknown') {
                setDragItem(null);
                return;
            }

            const isSameDroppable = source.droppableId === destination.droppableId;
            const chamadaId = Number(draggableId.split('-')[1]);

            if (!Number.isFinite(chamadaId)) {
                setDragItem(null);
                return;
            }

            // Bloquear drop em ramal que já tem ligação ativa
            if (destino.type === 'user' && !isSameDroppable) {
                const ligacoesDestino = dndList[destination.droppableId] ?? [];
                if (ligacoesDestino.length > 0) {
                    setDragItem(null);
                    return;
                }
            }

            // Se mover para o mesmo local, apenas reordenar visualmente
            if (isSameDroppable) {
                const items = reorder(
                    dndList[source.droppableId] ?? [],
                    source.index,
                    destination.index,
                );

                setDndList(prev => ({
                    ...prev,
                    [source.droppableId]: items,
                }));

                setDragItem(null);
                return;
            }

            // Mover para outro destino
            const novoEstado = move(
                dndList[source.droppableId],
                dndList[destination.droppableId],
                source,
                destination,
            );

            const destinoItens = novoEstado[destination.droppableId] ?? [];
            if (destinoItens[destination.index]) {
                destinoItens[destination.index] = {
                    ...destinoItens[destination.index],
                    usuario_responsavel_id:
                        destino.type === 'user' ? destino.id : null,
                    grupo_responsavel_id:
                        destino.type === 'group'
                            ? destino.id
                            : destino.type === 'waiting'
                                ? null
                                : destino.type === 'user'
                                    ? null
                                    : destinoItens[destination.index].grupo_responsavel_id,
                    estado: 'aguardando',
                };
                novoEstado[destination.droppableId] = destinoItens;
            }

            setDndList(prev => ({
                ...(prev ?? {}),
                ...(novoEstado ?? {}),
            }));

            if (destino.type === 'user') {
                transferMutator.mutate({
                    id: chamadaId,
                    usuario_id: destino.id,
                });
            } else if (destino.type === 'group') {
                transferMutator.mutate({
                    id: chamadaId,
                    grupo_id: destino.id,
                });
            } else {
                // Movendo para sala de espera (sem atendente/grupo)
                transferMutator.mutate({
                    id: chamadaId,
                });
            }
        },
        [
            transferMutator,
            dndList,
        ],
    );

    const totalLigacoesAtivas = queryList.data?.result?.length ?? 0;

    // Grupos do gestor logado para validação de acesso à chamada
    const gruposDoGestor = useMemo<number[]>(
        () => (user?.grupos ?? []).map((g: any) => g.id),
        [user],
    );

    // Retorna true se o gestor pode entrar nesta chamada (mesmo departamento ou sem departamento)
    const canEnterChamada = useCallback(
        (item: any) =>
            item.grupo_responsavel_id == null ||
            gruposDoGestor.includes(item.grupo_responsavel_id),
        [gruposDoGestor],
    );

    // Chamada que o gestor está monitorando no momento (por participantKey)
    const chamadaAtiva = useMemo<number | null>(() => {
        if (!ligacao?.participantKey) return null;
        const callIdPart = ligacao.participantKey.split(':')[0]; // "call-123"
        const parsed = parseInt(callIdPart.replace('call-', ''));
        return isNaN(parsed) ? null : parsed;
    }, [ligacao?.participantKey]);

    return (
        <LayoutUsuario>
            <div style={{ overflow: 'auto' }}>
                <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                        {/* Header */}
                        <HBox>
                            <VBox>
                                <Titulo>Filas de ligações</Titulo>
                                <Descricao>
                                    Gerencie as ligações ativas e transfira para outros atendentes ou departamentos.
                                    {totalLigacoesAtivas > 0 && (
                                        <span style={{ marginLeft: '8px', fontWeight: 'bold', color: 'var(--tc-color-primary)' }}>
                                            ({totalLigacoesAtivas} ligação{totalLigacoesAtivas !== 1 ? 'ões' : ''} ativa{totalLigacoesAtivas !== 1 ? 's' : ''})
                                        </span>
                                    )}
                                </Descricao>
                            </VBox>
                            <HBox gap="12px" className={styles.headerAcoes}>
                                <Botao
                                    variant="outline-primary"
                                    icon={mdiRefresh}
                                    onClick={() => queryList.refetch()}
                                    disabled={queryList.isFetching}
                                >
                                    Atualizar
                                </Botao>
                            </HBox>
                        </HBox>

                        {/* Filtros */}
                        <Grid>
                            <GridItem sm={12} md={4}>
                                <CampoTexto
                                    value={search}
                                    onChange={setSearch}
                                    prepend={
                                        <Icon
                                            path={mdiMagnify}
                                            size={1}
                                            color="var(--tc-color-gray-500)"
                                        />
                                    }
                                    placeholder="Pesquisar atendente"
                                />
                            </GridItem>
                            <GridItem sm={12} md={4}>
                                <FiltroGrupoUsuario
                                    value={filter.grupo_id.map(it => it.toString())}
                                    onChange={value =>
                                        setFilter({
                                            ...filter,
                                            grupo_id: value.map(it => parseInt(it)),
                                        })
                                    }
                                    disableQuery={!!dragItem}
                                />
                            </GridItem>
                            <GridItem sm={12} md={4}>
                                <CampoSeletor
                                    options={[
                                        { id: 'online', label: 'Somente atendentes online' },
                                        { id: 'all', label: 'Todos os atendentes' },
                                    ]}
                                    value={filter.somente_online ? 'online' : 'all'}
                                    onChange={value =>
                                        setFilter({
                                            ...filter,
                                            somente_online: value === 'online',
                                        })
                                    }
                                    noSearch
                                />
                            </GridItem>
                        </Grid>

                        {/* Conteudo */}
                        <VBox gap="16px" style={{ overflow: 'auto' }}>
                            <SalaEspera
                                droppableId="aguardando"
                                dragItem={dragItem}
                                nome="Sala de espera"
                                descricao="Aguardando atendente"
                                ligacoes={dndList['aguardando']}
                                chamadaAtiva={chamadaAtiva}
                                canEnterChamada={canEnterChamada}
                                aceitarLigacao={aceitarLigacao}
                                encerrarLigacao={encerrarLigacao}
                            />

                            {queryGroups.data?.result?.map((grupo: any) => (
                                <SalaEspera
                                    key={`grupo-${grupo.id}`}
                                    droppableId={`grupo-${grupo.id}`}
                                    dragItem={dragItem}
                                    nome={grupo.nome}
                                    descricao={grupo.descricao ?? 'Grupo / Departamento'}
                                    ligacoes={dndList[`grupo-${grupo.id}`]}
                                    hasOnlineAttendants={gruposComRamaisOnline.has(grupo.id)}
                                    chamadaAtiva={chamadaAtiva}
                                    canEnterChamada={canEnterChamada}
                                    aceitarLigacao={aceitarLigacao}
                                    encerrarLigacao={encerrarLigacao}
                                />
                            ))}

                            {queryUsers.data?.result?.map((user: any) => (
                                <SalaEspera
                                    key={`card-${user.id}`}
                                    dragItem={dragItem}
                                    nome={user.nome}
                                    online={user.online}
                                    droppableId={`atendente-${user.id}`}
                                    descricao={`Depto. ${user.grupos?.map((g: any) => g.nome)?.join(', ')}`}
                                    ligacoes={dndList[`atendente-${user.id}`]}
                                    requiresGrupo
                                    chamadaAtiva={chamadaAtiva}
                                    canEnterChamada={canEnterChamada}
                                    aceitarLigacao={aceitarLigacao}
                                    encerrarLigacao={encerrarLigacao}
                                />
                            ))}
                        </VBox>
                    </VBox>
                </DragDropContext>
            </div>

            {/* Painel de ligação flutuante (quando gestor está em uma chamada) */}
            {Boolean(ligacao) && (
                <Ligacao
                    ligacao={ligacao}
                    ligacaoState={ligacaoState}
                    clienteNome={ligacao!.canalClienteNome}
                    clienteImagem={ligacao!.canalClienteImagem}
                    onAtender={() => { }}
                    onEncerrar={(solucionado?: boolean) => encerrarLigacao(solucionado)}
                    onTransferir={() => { }}
                    onMinimizar={toggleMinimized}
                    isMinimizado={ligacaoState?.isMinimized || false}
                    tempoInicio={ligacaoState?.startDate || undefined}
                    isMuted={ligacaoState?.isMuted || false}
                    isSpeakerOn={ligacaoState?.isSpeakerOn || false}
                    onToggleMute={toggleMute}
                    onToggleSpeaker={toggleSpeaker}
                />
            )}
        </LayoutUsuario>
    );
};

export default Page;
